import { Equipment, Site } from '@energybox/react-ui-library/dist/types';
import { isDefined } from '@energybox/react-ui-library/dist/utils';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAnalyticsEnergyConsumptionPreviousPeriod as getSiteConsumptionPrevTimePeriod,
  getSiteConsumptionLastMonth,
  getSiteConsumptionSelectedTimePeriod,
  getSiteConsumptionThreeMonthsAgo,
  getSiteConsumptionTwoMonthsAgo,
} from '../../../actions/energy';
import { getEquipmentBySiteId } from '../../../actions/equipment';
import { getSite } from '../../../actions/sites';
import { getSpacesBySiteId } from '../../../actions/spaces';
import { useCurrentOrganizationId } from '../../../hooks/users';
import { useReportTimeFormats } from '../../../hooks/utils';
import { ApplicationState } from '../../../reducers';
import EnergyReportPage from '../EnergyReportPage';

type Props = {
  siteId: string;
  fromDate: string;
  toDate: string;
};

const EnergyReportPageContainer: React.FC<Props> = ({
  fromDate,
  toDate,
  siteId,
}) => {
  const dispatch = useDispatch();
  const siteIdNumber = parseInt(siteId);
  const orgId = useCurrentOrganizationId();

  const site = useSelector<ApplicationState, Site | undefined>(({ sites }) => {
    return sites.sitesById[siteId];
  });

  const reportTimeFormats = useReportTimeFormats(
    fromDate,
    toDate,
    site?.timeZone
  );
  const {
    fromDateInBrowserTimezone,
    toDateInBrowserTimezone,
    prevFromDateString,
    prevToDateString,
    reportDifferenceInDays,
    processing,
  } = reportTimeFormats;

  const [controller, setController] = useState(false);

  const equipments = useSelector<ApplicationState, Equipment[] | undefined>(
    ({ equipment }) => {
      return equipment.equipmentBySiteId[siteId];
    }
  );

  useEffect(() => {
    dispatch(getSite(siteIdNumber));
    dispatch(getEquipmentBySiteId(siteIdNumber));
    dispatch(getSpacesBySiteId(siteIdNumber));
  }, [siteIdNumber]);

  useEffect(() => {
    if (
      processing &&
      isDefined(prevFromDateString) &&
      isDefined(prevToDateString)
    ) {
      dispatch(
        getSiteConsumptionSelectedTimePeriod(
          fromDateInBrowserTimezone,
          toDateInBrowserTimezone,
          siteIdNumber
        )
      );
      dispatch(
        getSiteConsumptionPrevTimePeriod(
          prevFromDateString,
          prevToDateString,
          siteIdNumber
        )
      );
      setController(true);
    }
  }, [processing]);

  useEffect(() => {
    if (controller) {
      /* The timeout prevents deletion of dispatch in the caching of apiMiddleware.*/
      setTimeout(() => {
        dispatch(
          getSiteConsumptionLastMonth(toDateInBrowserTimezone, siteIdNumber)
        );
      }, 1000);
      dispatch(
        getSiteConsumptionTwoMonthsAgo(toDateInBrowserTimezone, siteIdNumber)
      );
      dispatch(
        getSiteConsumptionThreeMonthsAgo(toDateInBrowserTimezone, siteIdNumber)
      );
    }
  }, [prevFromDateString, prevToDateString, siteIdNumber, controller]);

  if (!site || !orgId) return <div />;

  return (
    <EnergyReportPage
      site={site}
      equipments={equipments}
      fromDate={fromDateInBrowserTimezone}
      toDate={toDateInBrowserTimezone}
      reportNumberOfDays={reportDifferenceInDays}
    />
  );
};

export default EnergyReportPageContainer;
